import { JHA_THEMES, setJhaWcTheme } from '@banno/jha-wc/src/styles/theme-provider';
import { setJhTheme } from '@jack-henry/jh-elements/utils/themeProvider';
import { DiContainer, InjectionToken } from '@jkhy/frontend-utils/di';
import {
    DynamicComponentExitReason,
    DynamicComponentRegistry,
} from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import { AppConfig, PlatformAppService } from '@jkhy/platform-tools/services/platform-app-service';
import { GraphService as WebUiGraphService } from '@jkhy/web-ui-graph/service';
import { NavigationsClient } from '@treasury/api/bo';
import { ConfigurationService } from '@treasury/core/config/configuration.service';
import { AppType, BuildType, BuildTypeToken } from '@treasury/core/config/configuration.types';
import { TmBffGraphHttpClient } from '@treasury/core/http/tm-bff-graph-http-client';
import { TmBffHttpClient } from '@treasury/core/http/tm-bff-http-client';
import { TmHttpClient } from '@treasury/core/http/tm-http-client';
import { LoggingService, RemoteLogPluginToken } from '@treasury/core/logging/logging.service';
import { NavigationService } from '@treasury/core/navigation/navigation.service';
import { XperienceService } from '@treasury/core/xperience';
import '@treasury/domain-components/feature-flags/feature-flag-button';
import { openIdentityDialog } from '@treasury/domain-components/identity-verification-dialog';
import { EntitlementsService } from '@treasury/domain/entitlements/entitlements.service';
import { AuthenticationService } from '@treasury/domain/services/authentication/authentication.service';
import { FeatureFlagService } from '@treasury/domain/services/feature-flags/feature-flag.service';
import { Feature, FeatureUser } from '@treasury/domain/services/feature-flags/feature-flag.types';
import { BoVisibilityFeatureConditionMap } from '@treasury/domain/services/feature-visibility/conditions/feature-visibility.backoffice.data';
import { FeatureVisibilityService } from '@treasury/domain/services/feature-visibility/feature-visibility.service';
import {
    GraphService,
    InternalUsersClient,
    NullGraphService,
    NullWiresGraphService,
} from '@treasury/domain/services/graph';
import { LogoutManager } from '@treasury/domain/services/logout';
import {
    OpenIdleDialogToken,
    type IdleDialogResult,
    type OpenIdleDialogFn,
} from '@treasury/domain/services/logout/idle-signout-service';
import { OpenTimeAccessDialogToken } from '@treasury/domain/services/logout/time-access-logout';
import { TmHttpClientMfa } from '@treasury/domain/shared/fetch-with-auth/tm-http-client-mfa';
import { OpenIdentityDialogToken } from '@treasury/domain/shared/fetch-with-auth/tm-http-client-mfa.types';
import { openTimeAccessDialog } from '@treasury/omega/domain-components/time-access-logout-dialog';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { ModernDialogService } from '@treasury/presentation/services/dialog/modern-dialog.service';
import { TmSideDrawerService } from '@treasury/presentation/services/side-drawer/side-drawer.service';
import {
    DrawerConfirmationPromptToken,
    SideDrawerPromptFn,
} from '@treasury/presentation/services/side-drawer/side-drawer.service.types';
import { ThemeService } from '@treasury/presentation/services/theme';
import { TmToastPosture } from '@treasury/presentation/services/toast';
import { TmToastService } from '@treasury/presentation/services/toast/tm-toast.service';
import colors from '@treasury/presentation/styles/colors.css?inline';
import baseStyles from '@treasury/presentation/styles/styles.css?inline';
import { SessionStorageService } from '@treasury/utils/services/session-storage.service';
import { WindowService } from '@treasury/utils/services/window.service';
import {
    logError as sdkLogError,
    logInfo as sdkLogInfo,
    setLogInstitutionId,
    setLogUserContext,
} from '../../artemis/src/sdk';
import { getTokenFromJhIdentity } from './authGuard';
import { BackOfficeApp } from './back-office-app';
import mainStyles from './main.css?inline';
import { BoNavigationService } from './services/bo-navigation-service';

function setStyles() {
    const colorStyleSheet = new CSSStyleSheet();
    colorStyleSheet.replaceSync(colors);
    const baseStyleSheet = new CSSStyleSheet();
    baseStyleSheet.replaceSync(baseStyles);
    const mainStyleSheet = new CSSStyleSheet();
    mainStyleSheet.replaceSync(mainStyles);

    document.adoptedStyleSheets = [
        ...document.adoptedStyleSheets,
        baseStyleSheet,
        colorStyleSheet,
        mainStyleSheet,
    ];
}

export const registerDI = async () => {
    const di = new DiContainer();
    const openDialogForSideDrawer: SideDrawerPromptFn = async (prompt, heading) => {
        const di = await DiContainer.getInstance();
        const dialogService = await di.getAsync(TmDialogService);
        return dialogService.open(prompt, heading);
    };

    di.provide(TmHttpClient, TmHttpClientMfa);
    di.provide(DynamicComponentRegistry, new DynamicComponentRegistry(true));
    di.provide(
        ConfigurationService,
        new ConfigurationService(
            AppType.BackOffice,
            BuildTypeToken.getValue() ?? BuildType.Unknown,
            new WindowService(),
            new SessionStorageService(new WindowService())
        )
    );
    di.provide(OpenIdentityDialogToken as unknown as InjectionToken<unknown>, openIdentityDialog);
    // BOv2-specific idle dialog: uses ModernDialogService (not OmegaDialogService).
    // The shared openIdleDialog helper from omega assumes OmegaDialogService's 4-argument
    // open() signature, which is incompatible with ModernDialogService's 3-argument API.
    const boV2OpenIdleDialog: OpenIdleDialogFn = async () => {
        const dialogService = di.get(TmDialogService);
        const resultPromise = dialogService.open(
            `For your protection, you will be logged out automatically due to inactivity. ` +
                `If you would like to remain logged in, please click 'Continue Working' below to ` +
                `keep your session active.`,
            'Session Timeout',
            {
                preventClose: false,
                buttons: [
                    {
                        reason: DynamicComponentExitReason.Confirm,
                        label: 'Log Out',
                    },
                    {
                        reason: DynamicComponentExitReason.Cancel,
                        label: 'Continue Working',
                    },
                ],
            }
        );
        const result: IdleDialogResult = {
            getDetail: async () => {
                const { reason } = await resultPromise;
                return reason === DynamicComponentExitReason.Confirm ? 'logout' : 'continue';
            },
            close: () => dialogService.close(DynamicComponentExitReason.ForceClose),
        };
        return result;
    };
    di.provide(OpenIdleDialogToken as unknown as InjectionToken<unknown>, boV2OpenIdleDialog);
    di.provide(DrawerConfirmationPromptToken, openDialogForSideDrawer);
    di.provide(
        OpenTimeAccessDialogToken as unknown as InjectionToken<unknown>,
        openTimeAccessDialog
    );
    di.provide(NavigationService, BoNavigationService);
    di.provide(TmDialogService, ModernDialogService);

    const config = di.get(ConfigurationService);
    if (config.bannoAuthEnabled) {
        di.provideFactory(TmBffGraphHttpClient, [TmBffHttpClient], httpClient => {
            return new TmBffGraphHttpClient(httpClient);
        });
        di.provideFactory(
            GraphService,
            [TmBffHttpClient, TmBffGraphHttpClient],
            (httpClient, graphHttpClient) => {
                // Both copies of @jkhy/web-ui-graph are the same version but resolve
                // to different physical paths in backoffice-v2 vs domain node_modules.
                // TypeScript treats them as structurally incompatible; cast to align.
                const webUiGraphService = new WebUiGraphService(
                    graphHttpClient
                ) as unknown as ConstructorParameters<typeof GraphService>[1];
                const internalUsersClient = new InternalUsersClient(httpClient);
                const wiresGraphService = new NullWiresGraphService();
                return new GraphService(
                    wiresGraphService,
                    webUiGraphService,
                    internalUsersClient,
                    config
                );
            }
        );
    } else {
        di.provide(GraphService, new NullGraphService() as unknown as GraphService);
    }

    di.provideFactoryAsync(
        FeatureFlagService,
        [
            TmHttpClient,
            SessionStorageService,
            ConfigurationService,
            AuthenticationService,
            LoggingService,
        ] as const,
        async (http, storage, config, auth, logger) => {
            const { loginId: userId, companyId } = await auth.authenticated$.toPromise();
            const ffService = new FeatureFlagService(http, storage, config, logger);
            const ffUser: FeatureUser = { userId, companyId, isAdmin: true, isSuperUser: true };
            await ffService.init(config.institutionId, ffUser);

            return ffService;
        }
    );
    di.provideFactoryAsync(
        FeatureVisibilityService,
        [EntitlementsService, AuthenticationService] as const,
        async (entitlements, auth) => {
            await auth.authenticated$.toPromise();
            const ffService = await di.getAsync(FeatureFlagService);
            const fvService = new FeatureVisibilityService(ffService, entitlements, auth);

            await fvService.init(BoVisibilityFeatureConditionMap);
            return fvService;
        }
    );
    if (import.meta.env.DEV) {
        di.provide(RemoteLogPluginToken, {
            setUserContext: (userId: string, companyId?: string) =>
                setLogUserContext({ userId, companyId }),
            setInstitutionId: (institutionId: string) => setLogInstitutionId(institutionId),
            logInfo: (message: string, context?: object) =>
                sdkLogInfo(message, context as Record<string, unknown>),
            logError: (message: string, error?: Error, context?: object) =>
                sdkLogError(message, error, context as Record<string, unknown>),
        });
    }

    di.provideFactory(LoggingService, [WindowService, ConfigurationService], (window, config) => {
        const remotePlugin = import.meta.env.DEV ? di.get(RemoteLogPluginToken) : undefined;
        const logger = new LoggingService(config, window, remotePlugin);
        logger.init();

        return logger;
    });

    di.provideFactoryAsync(PlatformAppService, [ConfigurationService] as const, async config => {
        const appConfig = Object.freeze<AppConfig>({
            requiresInstitutionId: config.bannoAuthEnabled,
            basePath:
                config.basePath.trim().length > 1
                    ? config.basePath.replace(/\/+$/, '')
                    : config.basePath,
            unauthenticatedApp: !config.bannoAuthEnabled,
        });
        return PlatformAppService.getInstance(appConfig);
    });

    await DiContainer.init(di);

    // set debug state out-of-band so logging service can be consumed synchronously
    (async () => {
        try {
            const ffService = await di.getAsync(FeatureFlagService);
            const [logger, isDebugEnabled] = await Promise.all([
                di.getAsync(LoggingService),
                ffService.isEnabled(Feature.DebugMode),
            ]);
            logger.isDebugEnabled = isDebugEnabled;
        } catch (error) {
            const logger = await di.getAsync(LoggingService).catch(() => undefined);
            logger?.logError(
                'Failed to configure debug logging',
                error instanceof Error ? error : new Error(String(error))
            );
        }
    })();

    return di;
};

function prefetchHighTrafficRoutes() {
    const prefetch = () => {
        void import('./dashboard/container');
        void import('./search/company/container');
    };
    if ('requestIdleCallback' in window) {
        requestIdleCallback(prefetch);
    } else {
        setTimeout(prefetch, 1000);
    }
}

/**
 * Initializes the back office application by setting up dependency injection and core services.
 * - Initializes the navigation service and router outlet
 * - Resolves all core services concurrently (theme, http, dialog, toast, side drawer, auth)
 * - Applies theme and stylesheet configurations
 * - Sets up event subscriptions for session expiration, route changes, and authentication
 * - Configures default dialog settings
 * - Starts logout timers after authentication
 *
 * @param di - The initialized DI container
 */
export const bootstrap = async (di: DiContainer) => {
    // Tracks the logged-in user across login/logout events for remote logging
    let remoteLogUser: { loginId: string; institutionId: string } | undefined;

    // Kick off all independent resolutions concurrently
    const navigationPromise = di.getAsync(NavigationService);
    const themeServicePromise = di.getAsync(ThemeService);
    const httpPromise = di.getAsync(TmHttpClient);
    const sideDrawerPromise = di.getAsync(TmSideDrawerService);
    const toastServicePromise = di.getAsync(TmToastService);
    const authServicePromise = di.getAsync(AuthenticationService);
    const dialogServicePromise = di.getAsync(TmDialogService);
    const jhaThemePromise = setJhaWcTheme(JHA_THEMES.DESIGN_SYSTEM);
    setJhTheme();

    // Await setOutlet first so the router is ready before other services resolve.
    const navigationService = await navigationPromise;
    await navigationService.setOutlet(BackOfficeApp.getOutlet);

    // Collect remaining resolutions that ran concurrently with setOutlet.
    const [themeService, http, sideDrawerService, toastService, authService, dialogService] =
        await Promise.all([
            themeServicePromise,
            httpPromise,
            sideDrawerPromise,
            toastServicePromise,
            authServicePromise,
            dialogServicePromise,
            jhaThemePromise,
        ]);

    // Set theme service to match browser preference.
    themeService.removeStoredTheme();

    // Load stylesheets.
    setStyles();

    // Subscribe to session expiration events to trigger logout and show a toast message.
    http.sessionExpired$.subscribe(() => onSessionEnd(di));

    // Set side drawer to close on route changes.
    navigationService.routeChange$.subscribe(() => sideDrawerService.close());

    // When logout is handled through the logout manager, navigate to login and show toast on forced logout.
    try {
        const logoutManager = await di.getAsync(LogoutManager);
        const xperienceService = await di.getAsync(XperienceService).catch(() => undefined);
        logoutManager.logout$.subscribe(async reason => {
            if (remoteLogUser) {
                const loggingService = await di.getAsync(LoggingService);
                const { loginId, institutionId } = remoteLogUser;
                loggingService.logInfo(
                    `User logout | user: ${loginId} | fi: ${institutionId} | reason: ${reason ?? 'manual'}`,
                    { userId: loginId, institutionId, reason: reason ?? 'manual' }
                );
            }
            // When hosted in the Xperience shell, re-run SSO instead of the manual login form.
            if (xperienceService?.isXperienceFrameworkHosted) {
                navigationService.navigate('/xperience/login');
                return;
            }
            navigationService.navigate('/login');
            if (reason === 'Forced' || reason === 'Restricted') {
                toastService.show('Your session has expired. Please log in again.');
            }
        });
    } catch (e) {
        const logger = await di.getAsync(LoggingService).catch(() => undefined);
        logger?.logError(
            'Failed to initialize LogoutManager',
            e instanceof Error ? e : new Error(String(e))
        );
        // eslint-disable-next-line no-console
        console.error('Error initializing LogoutManager', e);
    }

    // Start logout timers after each authentication.
    authService.authenticated$.subscribe(async () => {
        const logoutManager = await di.getAsync(LogoutManager);
        logoutManager.startLogoutTimers();

        prefetchHighTrafficRoutes();

        const loggingService = await di.getAsync(LoggingService);
        const config = await di.getAsync(ConfigurationService);
        const { loginId, companyId } = await authService.getCurrentUser();
        remoteLogUser = { loginId, institutionId: config.institutionId };
        loggingService.setRemoteUserContext(loginId, companyId);
        loggingService.logInfo(`User login | user: ${loginId} | fi: ${config.institutionId}`, {
            userId: loginId,
            institutionId: config.institutionId,
        });

        const httpClient = await di.getAsync(TmHttpClient);
        const navigations = new NavigationsClient(httpClient);
        const response = await navigations.navigationsGet();
        const { institutionUniversalId } = response.data as unknown as {
            institutionUniversalId: string;
        };
        config.institutionUniversalId = institutionUniversalId;
    });

    // Set default dialog service options for the app.
    dialogService.setDefaultConfiguration({
        orientation: 'horizontal',
        showCloseButton: true,
    });
};

/**
 * Handles session expiry by triggering a forced logout.
 * Navigation and toast messaging are handled reactively via logoutManager.logout$.
 * If banno auth is enabled, delegates session management to jh identity and reloads the page.
 *
 * @param di - The initialized DI container
 * @param reload - Optional function to reload the page. Defaults to window.location.reload (used in tests).
 */
export const onSessionEnd = async (
    di: DiContainer,
    reload: () => void = () => window.location.reload()
) => {
    try {
        const config = await di.getAsync(ConfigurationService);
        if (config.bannoAuthEnabled) {
            /* allow jhid to manage session time */
            await getTokenFromJhIdentity(di);
            reload();
            return;
        }
        const logoutManager = await di.getAsync(LogoutManager);
        await logoutManager.logOut('Forced');
    } catch (error) {
        // logging, navigation, and toast messaging are handled asynchronously
        di.getAsync(NavigationService).then(navigationService => {
            navigationService.navigate('/login');
        });
        di.getAsync(LoggingService).then(loggingService => {
            loggingService.logError(
                'Error during session expiration handling',
                error instanceof Error ? error : new Error(String(error))
            );
        });
        di.getAsync(TmToastService).then(toastService => {
            toastService.show(
                'An error occurred during session expiration. Please log in again.',
                TmToastPosture.Danger
            );
        });
    }
};
